import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '@/components/LocalizedLink';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { useSiteLogo } from '@/hooks/useSiteLogo';
import { DesktopNav, MobileNav } from '@/components/nav/NavLinks';

const NAV_ITEMS = [
  ['/', 'home'], ['/about', 'about'], ['/products', 'products'], ['/why-choose-us', 'whyUs'],
  ['/export-markets', 'exportMarkets'], ['/blog', 'blog'], ['/contact', 'contact'],
];

function useScrolled(threshold) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const scrolled = useScrolled(30);
  const logoUrl = useSiteLogo();
  const { pathname } = useLocation();
  const { lang, toggleLang, t, basePath } = useLanguage();

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  const navProps = {
    links: NAV_ITEMS.map(([to, key]) => ({ to, label: t.nav[key] })),
    activePath: basePath,
    lang,
    onToggleLang: toggleLang,
    quoteLabel: t.nav.getQuote,
  };

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md border-b border-saffron-100' : 'bg-white/80 backdrop-blur-sm'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-3">
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <img src={logoUrl} alt="ASJK Laxmi Global" width={1179} height={1085} className="h-14 w-auto max-w-[220px] object-contain mix-blend-multiply" data-testid="nav-logo-image" />
          </Link>
          <DesktopNav {...navProps} />
          <button className="lg:hidden text-earth-dark p-2" onClick={() => setMobileOpen(open => !open)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {mobileOpen && <MobileNav {...navProps} />}
    </nav>
  );
}
